import React, { useRef } from "react";
import { TableRowData } from "./data-table";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { MovementActivity } from "../lib/api";
import { format } from "date-fns";

const BLANK = "_______________________________";
const BLANK_ITEM_ROWS = 15;

interface SellNoteDrawerProps {
  movementData: MovementActivity | undefined;
  sellNoteDetails: TableRowData | null;
  blank?: boolean;
  onClose: () => void;
}

const SellNoteDrawer: React.FC<SellNoteDrawerProps> = ({
  movementData,
  sellNoteDetails,
  blank = false,
  onClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const sourceName = blank
    ? BLANK
    : movementData?.sourceSite
      ? [
          movementData.sourceSite.customer?.fullName,
          movementData.sourceSite.name,
        ]
          .filter(Boolean)
          .join(" - ")
      : movementData?.sourceWarehouse?.name ||
        movementData?.sourceVendor?.fullName ||
        "N/A";

  const destinationName = blank
    ? BLANK
    : movementData?.destinationSite
      ? [
          movementData.destinationSite.customer?.fullName,
          movementData.destinationSite.name,
          movementData.destinationSite.customer?.phoneNumber,
        ]
          .filter(Boolean)
          .join(" , ")
      : movementData?.destinationWarehouse?.name ||
        movementData?.destinationCustomer?.fullName ||
        "N/A";

  const getLocationLine = (
    entity:
      | { street?: string; ward?: string; district?: string; region?: string }
      | null
      | undefined,
  ) =>
    entity
      ? [entity.street, entity.ward, entity.district, entity.region]
          .filter(Boolean)
          .join(", ") || "N/A"
      : "N/A";
  const destinationLocation = blank
    ? BLANK
    : getLocationLine(
        movementData?.destinationSite ??
          movementData?.destinationWarehouse ??
          movementData?.destinationCustomer ??
          undefined,
      );

  const referenceNo = blank ? BLANK : movementData?.reference || "N/A";

  const displayDate = blank
    ? BLANK
    : movementData?.plannedDate
      ? format(new Date(movementData.plannedDate), "MMM dd, yyyy")
      : movementData?.createdAt
        ? format(new Date(movementData.createdAt), "MMM dd, yyyy")
        : "N/A";

  const driverName = blank
    ? BLANK
    : (movementData?.driverName ?? movementData?.driver_name ?? BLANK);
  const driverPhone = blank
    ? BLANK
    : (movementData?.driverPhoneNumber ??
      movementData?.driver_phone_number ??
      BLANK);
  const vehicleNo = blank
    ? BLANK
    : (movementData?.vehicleNumber ?? movementData?.vehicle_number ?? BLANK);

  const preparedBy = blank ? BLANK : movementData?.initiator?.fullName || "N/A";
  const captureDate = blank
    ? BLANK
    : movementData?.createdAt
      ? format(new Date(movementData.createdAt), "hh:mm a")
      : "N/A";

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = blank
      ? "Blank Sell Note"
      : "Sell Note To " + destinationName;
    window.print();

    document.title = originalTitle;
    if (parent) {
      parent.insertBefore(el, next);
    } else {
      el.remove();
    }
  };

  return (
    <SidePanel
      open={!!sellNoteDetails}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {sellNoteDetails && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={
              blank
                ? "Blank Sell Note"
                : `Sell Note from ${sourceName} to ${destinationName}`
            }
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                pinned: true,
              },
            ]}
          />
          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        SELL NOTE
                      </h1>
                    </div>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm font-bold text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm font-bold text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm font-bold text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-s font-bold text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>
                    <div className="flex items-start gap-6">
                      <div className="flex-1 border-2 border-gray-950 p-4 rounded-2xl">
                        <p className="text-gray-950 font-medium">
                          <span className="font-bold">M/S: </span> To{" "}
                          {destinationName}
                        </p>
                        <p className="text-gray-950 font-medium">
                          <span className="font-bold">Location: </span>{" "}
                          {destinationLocation}
                        </p>
                        <p className="text-gray-950 font-medium">
                          <span className="font-bold">From: </span> {sourceName}
                        </p>
                      </div>
                      <div>
                        <p className="font-bold text-gray-950">
                          No: <span>{referenceNo}</span>
                        </p>
                        <p className="font-bold text-gray-950">
                          Date: <span>{displayDate}</span>
                        </p>
                        <p className="font-bold text-gray-950">
                          TIN: 124 - 154 - 243
                        </p>
                        <p className="font-bold text-gray-950">
                          VRN: 40 - 028 - 187-U
                        </p>
                      </div>
                    </div>

                    <p className="text-sm font-bold text-gray-950 py-4">
                      <i>Please find below the sold items:-</i>
                    </p>

                    <table className="w-full border-2 border-gray-950 text-sm mb-6">
                      <thead>
                        <tr>
                          <th className="border-2 border-gray-950 text-gray-950 px-2 text-left">
                            Qty
                          </th>
                          <th className="border-2 border-gray-950 text-gray-950 px-2 text-left">
                            Particulars
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {blank
                          ? Array.from({ length: BLANK_ITEM_ROWS }).map(
                              (_, index) => (
                                <tr key={index}>
                                  <td className="border-2 border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border-2 border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                </tr>
                              ),
                            )
                          : Array.isArray(movementData?.items) &&
                            movementData?.items?.map((item, index) => (
                              <tr key={item.id || index}>
                                <td className="border-2 border-gray-950 px-2">
                                  <p className="text-sm font-bold text-gray-950">
                                    {item?.totalQuantity || 0}
                                  </p>
                                </td>
                                <td className="border-2 border-gray-950 px-2">
                                  <p className="text-sm font-bold text-gray-950">
                                    {[
                                      item?.inventory?.description || "N/A",
                                      item?.inventory?.length === "undefined" ||
                                      item?.inventory?.length == null
                                        ? "---"
                                        : item.inventory.length,
                                    ]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>

                    <div className="border-2 border-gray-950 p-4 rounded-2xl mb-4">
                      <p className="text-gray-950 font-medium">
                        <span className="font-bold">Driver Name: </span>{" "}
                        {driverName}
                      </p>
                      <p className="text-gray-950 font-medium">
                        <span className="font-bold">Driver Phone No: </span>{" "}
                        {driverPhone}
                      </p>
                      <p className="text-gray-950 font-medium">
                        <span className="font-bold">Vehicle No.: </span>{" "}
                        {vehicleNo}
                      </p>
                    </div>

                    <div className="text-sm font-bold text-gray-950">
                      <p>
                        Acknowledged receipt of the above-mentioned goods in
                        good order and condition.
                      </p>
                      <div className="flex justify-between mt-4">
                        <div>
                          <p className="font-semibold">
                            Name: _____________________________________________
                          </p>
                        </div>
                        <div>
                          <p className="font-semibold">
                            Signature: __________________
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 text-xs text-gray-950 space-y-1">
                        <p>
                          Please check that the equipment count agrees with the
                          above. All errors are to be clearly noted. Failure to
                          do this assumes acceptance of the documentation.
                        </p>
                        {!blank && movementData?.notes?.trim() && (
                          <p>
                            <span className="font-bold">Notes: </span>
                            {movementData.notes.trim()}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-between mt-4">
                        <p className="text-gray-950">
                          Prepared by:{" "}
                          <span className="font-semibold">{preparedBy}</span>
                        </p>
                        <p className="text-gray-950">
                          Capture Date & Time:{" "}
                          <span className="font-semibold">{captureDate}</span>
                        </p>
                        <p className="text-gray-950">
                          Print Date:{" "}
                          <span className="font-semibold">
                            {format(new Date(), "MMM dd, yyyy")}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default SellNoteDrawer;
